// Demo mode sign-in help (PD-714 owner instruction; demodata stream PD-744). Served only when the
// installation runs with IS_DEMO; every other installation answers 404 and returns nothing.
import { z } from 'zod';

export const DEMO_ACCOUNTS_PATH = '/api/v1/demo/accounts';
export const PLATFORM_DEMO_ACCOUNTS_PATH = '/api/platform/v1/demo/accounts';
/** PD-754: opens the paired demo lobby kiosk in this browser (demo mode only). */
export const DEMO_KIOSK_PATH = '/api/v1/demo/kiosk';

/**
 * PD-776 (demosandbox stream): on a kiosk or visitor-pass answer in demo mode the API says how it ran:
 * 'active' = in this browser's sandbox (shown to this browser only, undone after the idle time in
 * DEMO_SANDBOX_IDLE_HEADER seconds), 'fallback' = the sandbox cap was reached, so the answer was shown
 * once and not kept, 'ended' = the journey ended and its sandbox was rolled back.
 */
export const DEMO_SANDBOX_HEADER = 'x-passage-demo-sandbox';
export const DEMO_SANDBOX_IDLE_HEADER = 'x-passage-demo-sandbox-idle';
export type DemoSandboxState = 'active' | 'fallback' | 'ended';

/** PD-777: a sample visitor the kiosk finds (reference plus the second check, here a name). */
export const DemoKioskSample = z.object({ reference: z.string(), check: z.string() });
export type DemoKioskSample = z.infer<typeof DemoKioskSample>;

const Bilingual = z.object({ en: z.string(), ar: z.string() });
export type Bilingual = z.infer<typeof Bilingual>;

export const DemoAccount = z.object({
  /** Stable manifest key, e.g. "reception" or "security-supervisor". */
  key: z.string(),
  label: Bilingual,
  /** What to try with this account. */
  description: Bilingual,
  email: z.string(),
  /** Path to open after signing in (the role's first workspace). */
  landing: z.string(),
  /** 'role' = one approved experience; 'specialist' = experience plus specialist roles; 'multi' = several. */
  group: z.enum(['role', 'specialist', 'multi', 'platform']),
});
export type DemoAccount = z.infer<typeof DemoAccount>;

export const DemoLink = z.object({
  key: z.string(),
  label: Bilingual,
  path: z.string(),
  /** Demo account key to sign in as before opening the path; null = a public page (pass, kiosk). */
  account: z.string().nullable(),
});
export type DemoLink = z.infer<typeof DemoLink>;

export const DemoAccountsResponse = z.object({
  /** Manifest version; bumped whenever accounts or their grants change. */
  version: z.number().int(),
  /** The one public demo password (documented as public; synthetic accounts only). */
  password: z.string(),
  accounts: z.array(DemoAccount),
  /** "Try these screens": pages that need no staff account (visitor pass, kiosk) or deep links. */
  links: z.array(DemoLink),
  /** PD-777: sample visitors to look up at the demo kiosk (empty when the dataset has none). */
  kioskSamples: z.array(DemoKioskSample).optional(),
  /** When the demo data was last rebuilt (ISO), or null when unknown. */
  generatedAt: z.string().nullable(),
});
export type DemoAccountsResponse = z.infer<typeof DemoAccountsResponse>;
